import { plural } from '@ipfabric/frontend-shared-assets/utils/plural';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import { TableButton } from '@ipfabric/ui-components/TableButton/TableButton';
import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import { createTextColumn } from '@ipfabric/utils';
import { unsafeAssert } from '@ipfabric/utils/types/unsafeAssert';
import { createFrontendOnlyColumn } from 'components/Table/columns/frontendOnlyColumn';
import { Table } from 'components/Table/components/Table';
import type { TableBulkAction } from 'components/Table/model/TableModel';
import { useFrontendLocalTable } from 'components/Table/model/useFrontendLocalTable';
import { useMemo, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import type {
  AimApplicationDraft,
  AimWorkloadDraft,
} from '../../../model/aimDrafts';
import { AimDataTransferManager } from '../dataTransfer/AimDataTransferManager';
import { AimWorkloadDialog } from '../dialogs/AimWorkloadDialog';
import { AimInvalidBanner } from './helpers/AimInvalidBanner';
import { useAimOrphanDetection } from './helpers/useAimOrphanDetection';
import { useAimRowActions } from './helpers/useAimRowActions';

type Props = {
  form: UseFormReturn<{
    aimWorkloads: AimWorkloadDraft[];
    aimApplications: AimApplicationDraft[];
  }>;
};

type WorkloadRow = AimWorkloadDraft & {
  interfacesText: string;
  applicationName: string;
  applicationOrphan: boolean;
};

export const AimWorkloadsTable = ({ form }: Props) => {
  const workloads = useWatch({ control: form.control, name: 'aimWorkloads' });
  const applications = useWatch({
    control: form.control,
    name: 'aimApplications',
  });

  const orphanDetection = useAimOrphanDetection(form as never);

  const [showOnlyInvalid, setShowOnlyInvalid] = useState(false);
  const [importExportOpen, setImportExportOpen] = useState(false);

  const actions = useAimRowActions<AimWorkloadDraft>({
    fieldName: 'aimWorkloads' as never,
    control: form.control as never,
    setValue: form.setValue as never,
    isReferenced: orphanDetection.workloadReferencingFlows,
    deleteConfirmLabel: (count) => ({
      header: `${count} ${plural(count, 'workload')} will be deleted`,
      subHeader:
        count === 1
          ? 'Do you really want to delete this workload?'
          : `Do you really want to delete ${count} selected workloads?`,
      btnConfirm: 'Delete',
      btnSecondaryConfirm: 'Cancel',
    }),
    blockedDeleteLabel: (count, refs) => ({
      header: `Cannot delete: ${count} of selected workloads ${plural(count, 'is', 'are')} referenced`,
      subHeader: `Referenced by ${refs} ${plural(refs, 'flow')}. Update or delete the referencing flows first.`,
      btnConfirm: 'OK',
    }),
  });

  const tableData = useMemo<WorkloadRow[]>(() => {
    const appById = new Map(applications.map((a) => [a.id, a]));
    return workloads.map((w) => ({
      ...w,
      interfacesText: w.interfaces?.join(', ') || '—',
      applicationName: appById.get(w.applicationId)?.name ?? w.applicationId,
      applicationOrphan: !appById.has(w.applicationId),
    }));
  }, [workloads, applications]);

  const displayedData = useMemo(
    () =>
      showOnlyInvalid
        ? tableData.filter((row) =>
            orphanDetection.workloadOrphanIds.has(row.id),
          )
        : tableData,
    [tableData, showOnlyInvalid, orphanDetection.workloadOrphanIds],
  );

  const columns = useMemo(
    () => [
      createTextColumn<WorkloadRow>({ key: 'name', title: 'Name' }),
      createTextColumn<WorkloadRow>({
        key: 'ipAddress',
        title: 'IP Address',
        renderContent: ({ row }: { row: WorkloadRow }) => ({
          content: row.ipAddress || '—',
        }),
      }),
      createTextColumn<WorkloadRow>({
        key: 'applicationName',
        title: 'Application',
        renderContent: ({ row }: { row: WorkloadRow }) => ({
          content: row.applicationOrphan ? (
            <Tooltip
              content={`References missing Application: ${row.applicationId}`}
            >
              <span className='inline-flex items-center gap-xs text-warning'>
                <Icon glyph='fa-exclamation-triangle' size='sm' />
                {row.applicationName}
              </span>
            </Tooltip>
          ) : (
            row.applicationName
          ),
        }),
      }),
      createTextColumn<WorkloadRow>({
        key: 'interfacesText',
        title: 'Interfaces',
      }),
      createFrontendOnlyColumn<WorkloadRow>({
        key: 'actions',
        disabledSort: true,
        title: 'Actions',
        render: (row) => (
          <div className='flex flex-row items-center gap-xs'>
            <TableButton
              dataTestId={`aim-workloads-${row.name}-edit-icon`}
              variant='plain'
              icon='fa-pen'
              size='small'
              onClick={() => actions.handleEdit(row)}
              type='button'
            />
            <TableButton
              dataTestId={`aim-workloads-${row.name}-remove-icon`}
              variant='plain'
              accent='negative'
              icon='fa-trash-can'
              size='small'
              onClick={() => actions.handleDelete(row.id)}
              type='button'
            />
          </div>
        ),
      }),
    ],
    [actions],
  );

  const bulkActions: TableBulkAction[] = useMemo(
    () => [
      {
        key: 'delete',
        title: 'Delete',
        accent: 'negative',
        icon: 'fa-trash-can',
        disableAutoDeselect: true,
        onClick: async ({ selectedRows, deselectAll }) => {
          const ok = await actions.handleBulkDelete(
            unsafeAssert<Set<unknown>, Set<string>>(selectedRows),
          );
          if (ok) {
            deselectAll();
          }
        },
      },
    ],
    [actions],
  );

  const frontendSimpleFilter = useFrontendLocalTable({
    columns,
    data: displayedData,
  });

  return (
    <Card className='flex flex-col flex-1 rounded-tl-none' noShadow>
      <AimInvalidBanner
        invalidCount={orphanDetection.workloadOrphanIds.size}
        showOnlyInvalid={showOnlyInvalid}
        onToggle={() => setShowOnlyInvalid(!showOnlyInvalid)}
        invalidLabel='workloads have unresolved Application references'
      />
      <Table
        borderless
        bulkActions={bulkActions}
        rowIdField='id'
        loadRowIdsForSelectAll='fromData'
        className='flex-1 bg-transparent'
        columns={columns}
        rowHeight={44}
        title='Workload list'
        menuItems={[
          {
            standalone: true,
            elementWithKey: (
              <Button
                key='add-new'
                dataTestId='aim-workloads-add'
                variant='outline'
                size='small'
                icon='fa-plus-circle'
                onClick={actions.handleAdd}
              >
                Add new
              </Button>
            ),
          },
          {
            standalone: true,
            elementWithKey: (
              <Button
                key='import-export'
                variant='outline'
                size='small'
                icon='fa-file-import'
                onClick={() => setImportExportOpen(true)}
              >
                Import data
              </Button>
            ),
          },
        ]}
        {...frontendSimpleFilter}
      />
      {actions.editedItem !== null && (
        <AimWorkloadDialog
          formType={actions.editedItem === 'new' ? 'add' : 'edit'}
          initialValues={
            actions.editedItem === 'new' ? undefined : actions.editedItem
          }
          availableApplications={applications}
          onClose={actions.handleCloseDialog}
          onSubmit={actions.handleSubmitDialog}
        />
      )}
      {actions.confirmDeleteDialogRenderer()}
      {importExportOpen && (
        <AimDataTransferManager
          triggeredFrom='workloads'
          form={form as never}
          onClose={() => setImportExportOpen(false)}
        />
      )}
    </Card>
  );
};
